import { type UIMessage, getToolName, isToolUIPart } from 'ai'

import { isToolRunning } from '../parts/ToolCallPart'
import type { ToolPart } from '../parts/ToolCallPart'
import {
  type ActionProposal,
  type ArtifactLinkPart,
  type AssistantMessagePart,
  type Clarification,
  type Dashboard,
  readActionProposal,
  readArtifactLink,
  readClarification,
  readDashboard
} from '../stream/dataParts'
import { isHiddenTool } from '../stream/toolCalls'

type TextPart = Extract<AssistantMessagePart, { type: 'text' }>

/**
 * The assistant-api tools whose call ends in an approval card (a `data-proposed-action` part): mutations, the
 * long-running actions (bulk export, merged report, charger diagnostics) and outbound email.
 */
const PROPOSAL_TOOLS: ReadonlySet<string> = new Set([
  'propose_mutation',
  'propose_bulk_export',
  'propose_merged_report',
  'propose_charger_diagnostics',
  'send_email'
])

/** The clarification tool; the `data-clarification` part carries what the reader needs, so the call is not shown. */
const REQUEST_CLARIFICATION_TOOL = 'request_clarification'

/**
 * What a tool call did, for the summary line of a run ("Ran 6 queries, searched docs twice"):
 * - `query`: reads of Synop data, i.e. the `query` tool and the dedicated read tools (`get_*`).
 * - `docs`: `search_docs`.
 * - `dashboard`: the dashboard-building tools (`submit_dashboard_*`).
 * - `other`: every remaining tool (skills, files, billing drafts).
 */
export type ToolCategory = 'query' | 'docs' | 'dashboard' | 'other'

const TOOL_CATEGORIES: Readonly<Record<string, ToolCategory>> = {
  query: 'query',
  get_network_health_scorecard: 'query',
  get_charger_meter_values: 'query',
  get_billing_setup_overview: 'query',
  get_action_status: 'query',
  search_docs: 'docs',
  submit_dashboard_layout: 'dashboard',
  submit_dashboard_panel: 'dashboard'
}

/** Calls per category within one run; categories with no calls are absent. */
export type ToolCategoryCounts = Partial<Record<ToolCategory, number>>

/** Consecutive visible tool calls. A lone call is a run of one. */
export type ToolRun = {
  /** Stable React key, derived from the run's first tool call id. */
  key: string
  /** The calls in message order, for per-call rows. */
  parts: ToolPart[]
  counts: ToolCategoryCounts
  /** At least one call has no output yet: streaming, executing, or awaiting or answering an approval. */
  running: boolean
  /** At least one call ended in `output-error` or `output-denied`. */
  failed: boolean
}

/** An intermediate step between the prompt and the final response. */
export type TurnStep =
  /** One reasoning part, with its text and whether it is still streaming. */
  | { kind: 'thought'; key: string; text: string; streaming: boolean }
  /** Text the assistant wrote before its final response, between tool calls or reasoning. */
  | { kind: 'narration'; key: string; part: TextPart }
  | ({ kind: 'toolRun' } & ToolRun)
  /** A call to one of `PROPOSAL_TOOLS`. It is never counted in a run and always ends the run before it. */
  | { kind: 'proposal'; key: string; part: ToolPart }

/**
 * A part of the turn that belongs to the answer area, whichever position it holds in the stored parts. assistant-api
 * appends data parts to the end of the stored message while live they arrive mid-stream.
 */
export type AnswerPart =
  | { kind: 'proposal'; key: string; proposal: ActionProposal }
  | { kind: 'clarification'; key: string; clarification: Clarification }
  | { kind: 'dashboard'; key: string; dashboard: Dashboard }
  | { kind: 'artifactLink'; key: string; artifact: ArtifactLinkPart }

/**
 * How much there is to fold under a "Worked" header:
 * - `worked`: any visible tool run, proposal step or narration.
 * - `thought-only`: reasoning, with nothing else before the final response.
 * - `none`: nothing but the response.
 */
export type TurnShape = 'worked' | 'thought-only' | 'none'

export type TurnModel = {
  shape: TurnShape
  /** Intermediate steps in message order. */
  steps: TurnStep[]
  /** The text parts after the last reasoning part or shown tool call (hidden and clarification calls do not count); empty while the response has not started. */
  finalResponse: TextPart[]
  /** Proposals, clarifications, dashboards and artifact links in message order. */
  answerParts: AnswerPart[]
}

/**
 * Splits an assistant message's parts into intermediate steps, the final response and the answer-area parts.
 * Step boundaries, hidden tools, `request_clarification` calls and parts with no renderer are dropped; they neither
 * split a run, count in one, nor end the narration before the final response. Blank text parts are dropped too.
 */
export function buildTurnModel(parts: UIMessage['parts']): TurnModel {
  const lastWorkIndex = parts.findLastIndex(isVisibleWork)
  const steps: TurnStep[] = []
  const finalResponse: TextPart[] = []
  const answerParts: AnswerPart[] = []
  let run: ToolPart[] = []

  const closeRun = () => {
    const step = toToolRun(run)
    if (step) steps.push(step)
    run = []
  }

  parts.forEach((part, index) => {
    const answerPart = toAnswerPart(part, index)
    if (answerPart) {
      answerParts.push(answerPart)
      return
    }
    if (isToolUIPart(part)) {
      if (!isShownTool(part)) return
      if (PROPOSAL_TOOLS.has(getToolName(part))) {
        closeRun()
        steps.push({ kind: 'proposal', key: `proposal-step-${part.toolCallId}`, part })
        return
      }
      run.push(part)
      return
    }
    if (part.type === 'reasoning') {
      closeRun()
      steps.push({ kind: 'thought', key: `thought-${index}`, text: part.text, streaming: part.state === 'streaming' })
      return
    }
    if (part.type !== 'text' || !part.text.trim()) return
    if (index > lastWorkIndex) {
      finalResponse.push(part)
      return
    }
    closeRun()
    steps.push({ kind: 'narration', key: `narration-${index}`, part })
  })
  closeRun()

  return { shape: shapeOf(steps), steps, finalResponse, answerParts }
}

/** A tool call the model keeps: not hidden and not the clarification call. */
function isShownTool(part: ToolPart): boolean {
  const name = getToolName(part)
  return !isHiddenTool(name) && name !== REQUEST_CLARIFICATION_TOOL
}

/** A part that becomes an intermediate step: reasoning or a shown tool call. Dropped tools do not count. */
function isVisibleWork(part: AssistantMessagePart): boolean {
  return part.type === 'reasoning' || (isToolUIPart(part) && isShownTool(part))
}

function toolCategory(name: string): ToolCategory {
  return TOOL_CATEGORIES[name] ?? 'other'
}

function shapeOf(steps: TurnStep[]): TurnShape {
  if (steps.some((step) => step.kind !== 'thought')) return 'worked'
  return steps.length > 0 ? 'thought-only' : 'none'
}

function hasNoOutput(part: ToolPart): boolean {
  return isToolRunning(part) || part.state === 'approval-requested' || part.state === 'approval-responded'
}

function toToolRun(parts: ToolPart[]): TurnStep | undefined {
  const [first] = parts
  if (!first) return undefined
  const counts: ToolCategoryCounts = {}
  parts.forEach((part) => {
    const category = toolCategory(getToolName(part))
    counts[category] = (counts[category] ?? 0) + 1
  })
  return {
    kind: 'toolRun',
    key: `run-${first.toolCallId}`,
    parts,
    counts,
    running: parts.some(hasNoOutput),
    failed: parts.some((part) => part.state === 'output-error' || part.state === 'output-denied')
  }
}

function toAnswerPart(part: AssistantMessagePart, index: number): AnswerPart | undefined {
  const proposal = readActionProposal(part)
  if (proposal) return { kind: 'proposal', key: `proposal-${proposal.actionId}`, proposal }
  const clarification = readClarification(part)
  if (clarification) return { kind: 'clarification', key: `clarification-${index}`, clarification }
  const dashboard = readDashboard(part)
  if (dashboard) return { kind: 'dashboard', key: `dashboard-${dashboard.dashboardId}`, dashboard }
  const artifact = readArtifactLink(part)
  if (artifact) return { kind: 'artifactLink', key: `artifact-${artifact.actionId}`, artifact }
  return undefined
}
